import { useRef } from 'react';
import { Controller, type Path, type PathValue } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import AddressPanel from 'components/AddressPanel/AddressPanel';
import Button from 'components/Button/Button';
import DataSetsPanel from 'components/DataSetsPanel/DataSetsPanel';
import DatePicker from 'components/DatePicker/DatePicker';
import DetailSection from 'components/DetailSection/DetailSection';
import Loader from 'components/Loader/Loader';
import Modal from 'components/Modal/Modal';
import ModalMockFillButton from 'components/ModalMockFillButton/ModalMockFillButton';
import NumberField from 'components/NumberField/NumberField';
import Select from 'components/Select/Select';
import { DATA_SETS_ENUM_TYPES, UID_NAME_TUPLES } from 'constants/dataSets';
import {
  END_DATE,
  NACE_CODE, NACE_SECTION, NET_BOOK, NET_BOOK_CURRENCY, PROFIT_CENTER_IDS, START_DATE,
  VALUE_CHAIN_LIST,
  VALUE_CHAIN_SECTION,
} from 'constants/formFields';
import { DATE_FORMAT } from 'constants/general';
import { BUTTON_VARIANT, VALUE_CHAIN_UNIT_TYPES } from 'constants/interfaces';
import { useAiSubScreenContext } from 'contexts/AiScreenContext';
import useDataSetsDate from 'hooks/useDataSetsDate';
import useFieldsErrors from 'hooks/useFieldsErrors';
import useGeoLocations from 'hooks/useGeoLocations';
import useScrollToError from 'hooks/useScrollToError';
import useValueChainColumns from 'hooks/useValueChainColumns';
import useValueChainPosition from 'hooks/useValueChainPosition';
import { getBalanceSheetAssetsModalMockValues } from 'mocks/companyMapModalMocks';
import GeneralDataSetsPanel from 'pages/UserPages/CreateOwnOperations/components/GeneralDataSetsPanel';
import { useAppDispatch } from 'store/hooks/useApp';
import { useEnumQuery, useGetNaceCodeTreeQuery } from 'store/slices/dataSets/apis/dataSetsApi';
import { selectEnums, selectNaceCodes } from 'store/slices/dataSets/selectors';
import { setDataSetsState } from 'store/slices/dataSets/slice';
import { useUserMeQuery } from 'store/slices/user/apis/user';
import {
  useCreateBalanceSheetAssetsMutation,
  useGetBoardColumnNamesQuery, useLazyGetValueChainMapBoardQuery,
  useLazyGetValueChainMapBusinessModelQuery, useUidNameTuplesVCQuery, useUpdateBalanceSheetAssetsMutation,
} from 'store/slices/valueChainMap/apis/valueChainMapApi';
import { selectUidNameTuplesVC } from 'store/slices/valueChainMap/selectors';
import getRequestDataForOwnOperationsUpdate from 'utils/formHelpers/getRequestDataForOwnOperationsUpdate';
import { extractApiErrorMessage } from 'utils/helpers';

import useCreateBalanceSheetAssetsForm from './hooks/useCreateBalanceSheetAssetsForm';

import type IOption from 'components/MappingModal/interfaces/IOption';
import type ICustomer from 'store/slices/valueChainMap/interfaces/ICustomer';
import type { TCreateCustomerSchema, TCreateOperationsSchema } from 'utils/validators';
import type IBalanceSheetAssetsModal from './interfaces/IBalanceSheetAssetsModal';

import styles from './scss/BalanceSheetAssetsModal.module.scss';

function BalanceSheetAssetsModal({
  closeProfitCenterModal,
  valueChainMapId,
  setSelectedBalanceSheetAssets,
  currentTargetSwimlaneId,
  selectedBalanceSheetAssets,
  valueChainForm,
}: IBalanceSheetAssetsModal) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { setFieldsErrors } = useFieldsErrors();
  const placeholder = selectedBalanceSheetAssets?.id ? t('common.notProvided') : '';
  const modalRef = useRef<HTMLDivElement>(null);

  const [createBalanceSheetAssets,
    { isLoading: isCreateBalanceSheetAssetsLoading }] = useCreateBalanceSheetAssetsMutation();
  const [updateBalanceSheetAssets,
    { isLoading: isUpdateBalanceSheetAssetsLoading }] = useUpdateBalanceSheetAssetsMutation();
  const [fetchVCBusinessModel] = useLazyGetValueChainMapBusinessModelQuery();
  const [fetchVCBoard] = useLazyGetValueChainMapBoardQuery();

  const generateTitle = () => (selectedBalanceSheetAssets?.id
    ? t('ownOperations.editBalanceSheetAsset')
    : t('ownOperations.createNewBalanceSheetAsset'));

  // Open over the map: the assistant is told which unit is being edited, or that a new one is.
  useAiSubScreenContext({
    name: generateTitle(),
    customData: {
      unitType: VALUE_CHAIN_UNIT_TYPES.BALANCE_SHEET_ASSET,
      id: selectedBalanceSheetAssets?.id,
      name: selectedBalanceSheetAssets?.name,
    },
  });

  const { data: userData } = useUserMeQuery();
  const companyCurrency = userData?.data?.company?.currency;

  const {
    data: boardColumnNamesData,
    isLoading: isBoardColumnNamesLoading,
  } = useGetBoardColumnNamesQuery({ valueChainMapId });

  const handleGetCurrentColumn = (id?: number) => {
    let currentColumn: { id: number, name: string } = { id: 0, name: '' };
    if (id) {
      currentColumn = {
        id,
        name: boardColumnNamesData?.data?.find(
          (column) => column?.columnId === id,
        )?.columnName.split('. ')[1]
          || '',
      };
    }
    return currentColumn;
  };

  const { data: enumData, isLoading: isEnumLoading } = useEnumQuery({
    types: [DATA_SETS_ENUM_TYPES.currency, DATA_SETS_ENUM_TYPES.ownership, DATA_SETS_ENUM_TYPES.valueChainSection,
      DATA_SETS_ENUM_TYPES.businessUnitType, DATA_SETS_ENUM_TYPES.assetCategory, DATA_SETS_ENUM_TYPES.ownedLeased,
      DATA_SETS_ENUM_TYPES.upstreamTier, DATA_SETS_ENUM_TYPES.priorityRating],
  }, {
    selectFromResult: (result) => ({ ...result, data: selectEnums(result.data) }),
  });

  const { data: naceCodesData, isLoading: isNaceCodesTreeLoading } = useGetNaceCodeTreeQuery(undefined, {
    selectFromResult: (result) => ({ ...result, data: selectNaceCodes(result.data) }),
  });

  const { data: profitCenterIds, isLoading: isProfitCenterIdsLoading } = useUidNameTuplesVCQuery({
    types: [UID_NAME_TUPLES.profitCenter],
    valueChainMapId,
  }, {
    selectFromResult: (result) => ({ ...result, data: selectUidNameTuplesVC(result.data) }),
  });

  const isPageLoading = isEnumLoading || isNaceCodesTreeLoading || isCreateBalanceSheetAssetsLoading
   || isUpdateBalanceSheetAssetsLoading || isBoardColumnNamesLoading || isProfitCenterIdsLoading;

  const {
    control,
    handleSubmit,
    reset,
    setError,
    fieldsName,
    watch,
    setValue,
    formState,
    isUniqueIdentifierEditable,
  } = useCreateBalanceSheetAssetsForm({
    recordId: selectedBalanceSheetAssets?.id ? String(selectedBalanceSheetAssets.id) : '',
    isPageLoading,
    companyCurrency,
    valueChainSectionCurrent: handleGetCurrentColumn(currentTargetSwimlaneId)?.name,
  });

  const {
    handleGetVcOptions,
    vcOptionsList,
    listPosition,
  } = useValueChainPosition(watch(VALUE_CHAIN_LIST)?.value, valueChainMapId);

  const {
    dateOpened, onDateOpenedChange, onStartDateChange,
    startDate,
  } = useDataSetsDate<TCreateOperationsSchema>({ watch, setValue });

  const naceDivisions = watch(NACE_SECTION)?.divisions || [];

  const onNaceSelectionChange = () => {
    const emptyOption = null as PathValue<TCreateCustomerSchema, Path<TCreateCustomerSchema>>;
    setValue(NACE_CODE, emptyOption);
  };

  const {
    onFindCountries, onFindStates, onFindCities, isStatesDisabled, onChangeCountry, onChangeState,
    isCitiesDisabled, defaultCountries,
  } = useGeoLocations<TCreateOperationsSchema>({ watch, setValue });
  const profitCenterOptions = (
    profitCenterIds as Record<string, IOption[]> | undefined
  )?.[UID_NAME_TUPLES.profitCenter];

  const { getColumnId } = useValueChainColumns({
    enumData,
    boardColumnNamesData,
  });

  const { scrollToErrors } = useScrollToError({
    formState,
    scrollContainerRef: modalRef,
    scrollContainerSelector: '[class*="OwnOperationModalFormWrapper"]',
  });

  const isMockFillVisible = !selectedBalanceSheetAssets?.id && process.env.REACT_APP_API?.includes('api.dev');

  const onFillMockData = () => {
    reset({
      ...watch(),
      ...getBalanceSheetAssetsModalMockValues({
        enumData,
        naceCodesData,
        profitCenterOptions,
        companyCurrency,
        countryOptions: defaultCountries,
      }),
    });
  };

  const onCreateBalanceSheetAssetsSubmit = handleSubmit((values) => {
    if (selectedBalanceSheetAssets?.id) {
      updateBalanceSheetAssets({
        data: getRequestDataForOwnOperationsUpdate({
          values,
          recordId: `${selectedBalanceSheetAssets.id}`,
          valueChainForm,
          includeUniqueIdentifier: isUniqueIdentifierEditable,
        }),
        valueChainMapId,
        columnId: getColumnId(`${watch(VALUE_CHAIN_LIST)?.label}. ${watch(VALUE_CHAIN_SECTION)?.label}`)?.toString(),
      })
        .unwrap()
        .then(() => {
          if (valueChainMapId) {
            setTimeout(() => {
              fetchVCBusinessModel({ valueChainMapId });
              fetchVCBoard({ valueChainMapId });
            }, 10000);
          }
          closeProfitCenterModal();
        })
        .catch((errors) => {
          const hasFieldErrors = setFieldsErrors({ errors, setError, fieldsName });
          if (hasFieldErrors) {
            scrollToErrors();
          } else {
            toast.error(extractApiErrorMessage(errors) || t('common.unknownError'));
          }
        });
    } else {
      createBalanceSheetAssets({
        data: getRequestDataForOwnOperationsUpdate({ values, valueChainForm }),
        valueChainMapId,
        columnId: getColumnId(`${watch(VALUE_CHAIN_LIST)?.label}. ${watch(VALUE_CHAIN_SECTION)?.label}`)?.toString()
        || currentTargetSwimlaneId?.toString(),
      })
        .unwrap()
        .then(({ data }) => {
          setSelectedBalanceSheetAssets({ id: data.id, uniqueIdentifier: data.uniqueIdentifier } as ICustomer);
          dispatch(setDataSetsState({
            selectedCustomer: { label: data.uniqueIdentifier, value: data.uniqueIdentifier },
          }));
          if (valueChainMapId) {
            setTimeout(() => {
              fetchVCBusinessModel({ valueChainMapId });
              fetchVCBoard({ valueChainMapId });
            }, 10000);
          }
          closeProfitCenterModal();
        })
        .catch((errors) => {
          const hasFieldErrors = setFieldsErrors({ errors, setError, fieldsName });
          if (hasFieldErrors) {
            scrollToErrors();
          } else {
            toast.error(extractApiErrorMessage(errors) || t('common.unknownError'));
          }
        });
    }
  });

  return (
    <Modal
      open
      isButtonsVisible={false}
      className={styles.modal}
      title={generateTitle()}
      titleAction={isMockFillVisible ? (
        <ModalMockFillButton
          onClick={onFillMockData}
          disabled={isPageLoading}
        />
      ) : null}
    >
      <div ref={modalRef}>
        <div>
          <div className={styles.OwnOperationModalFormWrapper}>
            <Loader isPageLoading={isPageLoading} isFullLoader={false} />
            <GeneralDataSetsPanel
              control={control}
              enumData={enumData}
              recordId={selectedBalanceSheetAssets?.id ? String(selectedBalanceSheetAssets.id) : ''}
              isUniqueIdentifierEditable={isUniqueIdentifierEditable}
              placeholder={placeholder}
              dateOpened={dateOpened}
              onDateOpenedChange={onDateOpenedChange}
              setValue={setValue}
              vcOptionsList={vcOptionsList}
              handleGetVcOptions={handleGetVcOptions}
              listPosition={listPosition}
            />
            <DetailSection
              control={control}
              naceCodesData={naceCodesData}
              naceDivisions={naceDivisions}
              onNaceSelectionChange={onNaceSelectionChange}
              placeholder={placeholder}
              recordId={selectedBalanceSheetAssets?.id ? String(selectedBalanceSheetAssets.id) : ''}
            />
            <DataSetsPanel title={t('ownOperations.selections.financials')} className={styles.fullWidth}>
              <div className={styles.wrapper}>
                <Controller
                  control={control}
                  name={PROFIT_CENTER_IDS}
                  render={({
                    field: {
                      name,
                      value,
                      onChange,
                    },
                    fieldState: { error },
                  }) => (
                    <Select
                      name={name}
                      label={t('ownOperations.fields.profitCentreID')}
                      placeholder={selectedBalanceSheetAssets?.id
                        ? placeholder : t('ownOperations.fields.profitCentre')}
                      options={profitCenterIds?.[UID_NAME_TUPLES.profitCenter as keyof typeof profitCenterIds]}
                      value={value || undefined}
                      error={error?.message}
                      onChange={onChange}
                      menuPosition="absolute"
                      isSearchable
                      required
                      isMulti
                    />
                  )}
                />
              </div>
              <div className={styles.wrapper}>
                <Controller
                  name={START_DATE}
                  control={control}
                  render={({
                    field: {
                      name,
                      onChange,
                      value,
                    },
                    fieldState: { error },
                  }) => (
                    <DatePicker
                      label={t('ownOperations.fields.startDate')}
                      error={error?.message}
                      onChange={(date) => {
                        onStartDateChange(date);
                        onChange(date);
                      }}
                      value={value}
                      name={name}
                      maxDate={new Date()}
                      placeholder={DATE_FORMAT}
                      required
                    />
                  )}
                />
                <Controller
                  name={END_DATE}
                  control={control}
                  render={({
                    field: {
                      name,
                      value,
                      onChange,
                    },
                    fieldState: { error },
                  }) => (
                    <DatePicker
                      label={t('ownOperations.fields.endDate')}
                      placeholder={DATE_FORMAT}
                      name={name}
                      onChange={onChange}
                      minDate={startDate}
                      value={value}
                      error={error?.message}
                      required
                    />
                  )}
                />
              </div>
              <div className={styles.wrapper}>
                <Controller
                  control={control}
                  name={NET_BOOK}
                  render={({ field: { name, value, onChange }, fieldState: { error } }) => (
                    <div>
                      <div className={styles.fieldWithCurrency}>
                        <NumberField
                          label={t('ownOperations.fields.netBook')}
                          name={name}
                          value={value}
                          error={error?.message}
                          onChange={(values) => onChange(values.value)}
                          placeholder={t('ownOperations.fields.netBook')}
                          className={styles.valueInput}
                          allowNegative={false}
                          hideTextError
                          required
                        />
                        <Controller
                          control={control}
                          name={NET_BOOK_CURRENCY}
                          render={({
                            field:
                        { value: valueCurrency },
                          }) => (
                            <span className={`${styles.currencyInput} ${error?.message ? styles.errorBorder : ''}`}>
                              {`M, ${valueCurrency}`}
                            </span>
                          )}
                        />
                      </div>
                      {error?.message ? <span className={styles.errorMessage}>{error.message}</span> : null}
                    </div>
                  )}
                />
              </div>
            </DataSetsPanel>
            <AddressPanel
              control={control}
              placeholder={placeholder}
              onFindCountries={onFindCountries}
              onFindStates={onFindStates}
              onFindCities={onFindCities}
              isStatesDisabled={isStatesDisabled}
              isCitiesDisabled={isCitiesDisabled}
              onChangeCountry={onChangeCountry}
              onChangeState={onChangeState}
              menuPosition="absolute"
              defaultCountries={defaultCountries}
              requiredState
            />
          </div>
        </div>
        <div className={styles.buttons}>
          <Button
            title={t('common.cancel')}
            labelKey="common.cancel"
            variant={BUTTON_VARIANT.cancel}
            onClick={closeProfitCenterModal}
          />
          <div className={styles.buttonsRight}>
            {selectedBalanceSheetAssets?.id ? (
              <Button
                title={t('common.save')}
                labelKey="common.save"
                onClick={onCreateBalanceSheetAssetsSubmit}
                isLoading={isPageLoading}
              />
            ) : null}
            {!selectedBalanceSheetAssets?.id ? (
              <Button
                title={t('common.create')}
                labelKey="common.create"
                onClick={onCreateBalanceSheetAssetsSubmit}
                isLoading={isPageLoading}
              />
            ) : null}
          </div>
        </div>
      </div>
    </Modal>
  );
}

export default BalanceSheetAssetsModal;
